import React, { useEffect, useRef } from 'react';
import { Recording, Transaction } from '../../../types/rest/assetCatalogTypes';
import BlazeTable from '../../../components/blazeTable/BlazeTable';
import { BlazeTableColDef, BlazeTableOptions, SelectAllType } from '../../../components/blazeTable/BlazeTable.types';
import i18n from '../../../i18n';
import BlazeTableActions from '../../../components/blazeTable/BlazeTableActions';
import { Button } from 'react-bootstrap';
import BzmIcon from '../../../components/icons/BzmIcon';
import { useDispatch } from 'react-redux';
import RecordingEditor from './RecordingEditor';
import { getDefaultServiceId } from '../../../store/ui/service/serviceActions';
import _ from 'lodash';
import {
  changeRecordingPageSize,
  expandRecordingRow,
  setIsCreatingNewRecording,
  sortRecordings,
} from '../../../store/recordings/recordingsReducer';
import ExpandLink from '../../../components/blazeTable/ExpandLink';
import SimpleTagList from '../../../components/ui/tags/SimpleTagList';
import { generateDate } from '../../../utils/dateUtil';
import { Pageable, SortConfiguration } from '../../../types/rest/paginationTypes';
import { fetchRecordingsThunk, updateActiveRecordingPage } from '../../../store/recordings/recordingsActions';

interface RecordingsTableProps {
  recordings: Recording[];
  isCreatingNewRecording: boolean;
  expandedRecordingId: number | null;
  onSelectionChange: (selectedItems: Array<any>) => void;
  onDelete: (recording: Recording) => void;
  onConvert: (recording: Recording) => void;
  selectedItems: number[];
  isLoading: boolean;
  recordingPageable: Pageable;
  recordingSort: SortConfiguration;
  totalRecordings: number;
  selectAllType: null | SelectAllType;
}

const RecordingsTable = (props: RecordingsTableProps) => {
  const dispatch = useDispatch();
  const tableRef = useRef<BlazeTable>(null);

  const expandRecording = (recordingId: number | null) => {
    dispatch(expandRecordingRow(recordingId));
  };

  const getEmptyRecording = (): Recording => {
    const serviceId = dispatch(getDefaultServiceId(true));
    return {
      id: -1,
      serviceId: serviceId,
      serviceName: '',
      name: 'Recording-' + generateDate(),
      description: '',
      tags: [],
      messages: [],
      created: 0,
      updated: 0,
      createdBy: '',
      updatedBy: '',
      updatedDate: '',
      createdDate: '',
    };
  };

  const isRecordingNew = (recording: Recording) => {
    return recording.id === -1;
  };

  const deleteRowClicked = (recording: Recording) => {
    if (isRecordingNew(recording)) {
      dispatch(expandRecordingRow(null));
      dispatch(setIsCreatingNewRecording(false));
    } else {
      props.onDelete(recording);
    }
  };

  useEffect(() => {
    if (tableRef.current && props.isCreatingNewRecording) {
      tableRef.current.expandRowHandler(-1);
    }
  }, [props.isCreatingNewRecording]);

  const colsDefinition: BlazeTableColDef[] = [
    {
      header: 'Id',
      cellComponent: (recording: Recording) => {
        if (isRecordingNew(recording)) {
          return '-';
        }
        return <span>{recording.id}</span>;
      },
      isSortable: false,
      accessor: 'id',
      width: 80,
    },
    {
      header: 'Name',
      cellComponent: (recording: Recording) => {
        if (isRecordingNew(recording)) {
          return '-';
        }
        return (
          <div className="d-flex align-items-center">
            <div style={{ marginTop: '6px' }}>
              <BzmIcon
                title={i18n.t('recordings:table.recording')}
                variant={'icon-bzm-ct-recording'}
                className={'icon-xs mr-2'}
              />
            </div>
            <ExpandLink
              id={recording.id ? recording.id : ''}
              value={recording.name}
              onToggleExpandDetail={() => tableRef.current.expandRowHandler(recording.id)}
            />{' '}
          </div>
        );
      },
      accessor: 'name',
    },
    {
      header: 'Created Date',
      cellComponent: (recording: Recording) => {
        if (isRecordingNew(recording)) {
          return '-';
        }
        return <span>{recording.createdDate}</span>;
      },
      isSortable: false,
      accessor: 'createdDate',
    },
    {
      header: 'Created by',
      cellComponent: (recording: Recording) => {
        if (isRecordingNew(recording)) {
          return '-';
        }
        return <span>{recording.createdBy}</span>;
      },
      isSortable: false,
      accessor: 'createdBy',
    },
    {
      accessor: 'serviceName',
      cellComponent: (recording: Recording) => {
        if (isRecordingNew(recording)) {
          return '-';
        }
        return <span>{recording.serviceName}</span>;
      },
      isSortable: false,
      header: 'Service',
    },
    {
      header: 'Tags',
      cellComponent: (recording: Recording) => {
        return (
          <div className={`qa-tbl-row-transaction-tags-${recording.id} picked`}>
            {recording.tags != null && <SimpleTagList tags={recording.tags} />}
          </div>
        );
      },
      isSortable: false,
      accessor: 'tags',
    },
    {
      header: 'Actions',
      headerComponent: <div className={'text-center'}>Actions</div>,
      width: 100,
      cellClass: 'text-right',
      accessor: 'value',
      isSortable: false,
      cellComponent: (recording: Recording) => {
        return (
          <div className={'text-left'}>
            <BlazeTableActions>
              <Button className={'btn-link-dark btn-no-hover'}>
                <BzmIcon
                  variant={'icon-bzm-ct-assign'}
                  title={i18n.t('recordings:table.convert')}
                  className={'icon-xs'}
                  onClick={() => props.onConvert(recording)}
                />
              </Button>
              <Button className={'btn-link-dark btn-no-hover'}>
                <BzmIcon
                  variant={'icon-bzm-delete'}
                  title={i18n.t('recordings:table.delete')}
                  className={'icon-xs'}
                  onClick={() => deleteRowClicked(recording)}
                />
              </Button>
            </BlazeTableActions>
          </div>
        );
      },
    },
  ];

  const expandDetailComponent = (recording: Recording) => {
    return <RecordingEditor isCreate={isRecordingNew(recording)} recording={recording} isReadOnly={true} />;
  };

  const prepareData = (): Recording[] => {
    const recordings: Recording[] = _.clone(props.recordings);
    if (props.isCreatingNewRecording) {
      recordings.unshift(getEmptyRecording());
    }
    return recordings;
  };

  const tableOptions: BlazeTableOptions = {
    showRowExpander: true,
    expandDetailComponent: (rowData: any) => expandDetailComponent(rowData),
    columnDef: colsDefinition,
    pageable: true,
    selectable: true,
    pageSize: 10,
    sortable: true,
    selectAllDropdown: true,
    paginationOverride: {
      activePage: props.recordingPageable.skip,
      pageSize: props.recordingPageable.limit,
      total: props.totalRecordings,
    },
    sortingOverride: props.recordingSort,
    selectAllType: props.selectAllType,
    tableName: 'recordingsList',
    loadingText: i18n.t('recordings:table.loadingRecordings'),
    noResultsText: i18n.t('recordings:table.noResults'),
  };

  return (
    <BlazeTable
      onExpandedItemChange={(id: number | null) => {
        expandRecording(props.expandedRecordingId === id ? null : id);
      }}
      expandedItem={props.expandedRecordingId}
      selectedItems={props.selectedItems}
      onSelectionChange={props.onSelectionChange}
      options={tableOptions}
      data={prepareData()}
      isLoading={props.isLoading}
      ref={tableRef}
      onSort={sortConfig => {
        dispatch(sortRecordings(sortConfig));
        dispatch(fetchRecordingsThunk(true));
      }}
      showPageSizeDropdown={true}
      onPageChange={pageNumber => dispatch(updateActiveRecordingPage(pageNumber))}
      onPageSizeChange={pageSize => dispatch(changeRecordingPageSize(pageSize))}
    />
  );
};

export default RecordingsTable;
